import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { Trash2, ShieldAlert, Crown } from "lucide-react";
import { api, trackCoverUrl, eur, fmtError } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Reveal } from "../components/Reveal";

const Panier = () => {
  const { items, remove, total, clear } = useCart();
  const hasExclusive = items.some((i) => i.exclusive);
  const pct = hasExclusive ? 0 : items.length >= 5 ? 10 : items.length >= 3 ? 7 : items.length >= 2 ? 5 : 0;
  const discount = Math.round(total * pct) / 100;
  const finalTotal = Math.round((total - discount) * 100) / 100;
  const { user, checked } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [payError, setPayError] = useState("");
  const [cgvAccepted, setCgvAccepted] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/config/paypal").then((r) => setCfg(r.data)).catch(() => setCfg({ configured: false }));
  }, []);

  return (
    <main data-testid="cart-page" className="mx-auto max-w-5xl px-5 pb-24 pt-32 sm:px-8">
      <Reveal>
        <p className="text-xs uppercase tracking-[0.35em] text-amber-500">Panier</p>
        <h1 className="mt-3 font-display text-4xl font-black tracking-tighter sm:text-5xl">Vos licences</h1>
      </Reveal>

      {items.length === 0 ? (
        <div className="mt-16 border border-dashed border-white/15 p-16 text-center" data-testid="cart-empty">
          <p className="text-sm uppercase tracking-[0.25em] text-zinc-500">Votre panier est vide</p>
          <Link
            to="/catalogue"
            data-testid="cart-browse"
            className="mt-6 inline-block border border-amber-500/60 px-6 py-3 text-xs uppercase tracking-[0.25em] text-amber-400 transition-colors hover:bg-amber-500 hover:text-black"
          >
            Explorer le catalogue
          </Link>
        </div>
      ) : (
        <div className="mt-12 grid gap-10 lg:grid-cols-5">
          <div className="space-y-4 lg:col-span-3" data-testid="cart-items">
            {items.map((t) => (
              <Reveal key={t.track_id}>
                <div className={`flex items-center gap-4 border p-4 ${t.exclusive ? "border-amber-500/40 bg-amber-500/5" : "border-white/[0.08] bg-surface"}`} data-testid={`cart-item-${t.track_id}`}>
                  <img src={trackCoverUrl(t)} alt={t.title} className="h-16 w-16 border border-white/10 object-cover" />
                  <div className="min-w-0 flex-1">
                    <Link to={`/morceau/${t.track_id}`} className="block truncate font-display text-base font-medium transition-colors hover:text-amber-400">
                      {t.title}
                    </Link>
                    {t.exclusive ? (
                      <p className="mt-0.5 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-amber-400" data-testid="cart-exclusive-badge">
                        <Crown className="h-3 w-3" /> Licence exclusive — retrait du catalogue
                      </p>
                    ) : (
                      <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{(t.usages || []).join(" · ")}</p>
                    )}
                  </div>
                  <span className="text-sm font-bold text-amber-400">{eur(t.price)}</span>
                  <button
                    onClick={() => remove(t.track_id)}
                    data-testid={`cart-remove-${t.track_id}`}
                    className="flex h-9 w-9 items-center justify-center text-zinc-500 transition-colors hover:text-red-400"
                    title="Retirer"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.1} className="lg:col-span-2">
            <div className="border border-white/[0.08] bg-surface p-8">
              <div className="space-y-2 border-b border-white/[0.08] pb-5">
                <div className="flex items-center justify-between">
                  <span className="text-xs uppercase tracking-[0.25em] text-zinc-500">Sous-total</span>
                  <span data-testid="cart-subtotal" className="text-sm text-zinc-300">{eur(total)}</span>
                </div>
                {pct > 0 && (
                  <div className="flex items-center justify-between" data-testid="cart-discount">
                    <span className="text-xs uppercase tracking-[0.25em] text-emerald-400">Remise lot −{pct} %</span>
                    <span className="text-sm font-bold text-emerald-400">−{eur(discount)}</span>
                  </div>
                )}
                <div className="flex items-center justify-between border-t border-white/[0.08] pt-3">
                  <span className="text-xs uppercase tracking-[0.25em] text-zinc-500">Total</span>
                  <span data-testid="cart-total" className="font-display text-3xl font-black text-amber-500">{eur(finalTotal)}</span>
                </div>
              </div>
              {hasExclusive ? (
                <p className="mt-4 text-[11px] uppercase tracking-[0.15em] text-amber-400/80" data-testid="exclusive-note">
                  Exclusivité : paiement en une fois, morceau retiré de la vente après validation
                </p>
              ) : (
                <p className="mt-4 text-[11px] uppercase tracking-[0.15em] text-zinc-500" data-testid="discount-tiers">
                  Remises automatiques : dès 2 morceaux −5 % · dès 3 −7 % · dès 5 −10 %
                </p>
              )}
              <p className="mt-5 text-xs leading-relaxed text-zinc-500">
                Paiement sécurisé par PayPal. Les masters sont disponibles au téléchargement
                immédiatement après validation.
              </p>

              {!user && checked ? (
                <Link
                  to="/connexion"
                  data-testid="cart-login-redirect"
                  className="mt-6 block bg-amber-500 py-4 text-center text-xs font-bold uppercase tracking-[0.25em] text-black transition-colors hover:bg-amber-400"
                >
                  Connectez-vous pour payer
                </Link>
              ) : cfg?.configured ? (
                <div className="mt-6">
                  <label className="flex cursor-pointer items-start gap-3 border border-white/10 p-4" data-testid="cgv-checkbox">
                    <input
                      type="checkbox"
                      checked={cgvAccepted}
                      onChange={(e) => setCgvAccepted(e.target.checked)}
                      className="mt-0.5 h-4 w-4 shrink-0 accent-amber-500"
                    />
                    <span className="text-xs leading-relaxed text-zinc-400">
                      J'accepte les{" "}
                      <Link to="/cgv" className="text-amber-400 underline-offset-4 hover:underline" data-testid="cgv-link">
                        Conditions Générales de Vente
                      </Link>{" "}
                      et je reconnais que le contenu numérique est disponible immédiatement après paiement,
                      ce qui vaut renonciation à mon droit de rétractation.
                    </span>
                  </label>
                  {cgvAccepted ? (
                    <div className="mt-4" data-testid="paypal-buttons">
                  <PayPalScriptProvider options={{ clientId: cfg.client_id, currency: "EUR", intent: "capture" }}>
                    <PayPalButtons
                      style={{ layout: "vertical", color: "gold", shape: "rect", label: "pay" }}
                      forceReRender={[finalTotal]}
                      createOrder={async () => {
                        setPayError("");
                        try {
                          const { data } = await api.post("/orders/paypal/create", {
                            track_ids: items.map((i) => i.track_id),
                            exclusive: hasExclusive,
                          });
                          return data.order_id;
                        } catch (e) {
                          setPayError(fmtError(e));
                          throw e;
                        }
                      }}
                      onApprove={async (d) => {
                        try {
                          await api.post(`/orders/paypal/${d.orderID}/capture`);
                          clear();
                          toast.success("Paiement validé — vos masters sont disponibles");
                          navigate("/compte");
                        } catch (e) {
                          setPayError(fmtError(e));
                        }
                      }}
                      onError={() => setPayError("Le paiement PayPal a échoué. Réessayez.")}
                    />
                  </PayPalScriptProvider>
                    </div>
                  ) : (
                    <p className="mt-4 text-xs text-zinc-500" data-testid="cgv-required">
                      Cochez l'acceptation des CGV ci-dessus pour afficher le paiement sécurisé.
                    </p>
                  )}
                </div>
              ) : cfg && (
                <div className="mt-6 border border-amber-500/30 bg-amber-500/5 p-5" data-testid="paypal-not-configured">
                  <p className="flex items-start gap-3 text-xs leading-relaxed text-amber-300/90">
                    <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
                    Le paiement PayPal est en cours de configuration par le studio.
                    Revenez très vite pour finaliser votre licence.
                  </p>
                </div>
              )}
              {payError && (
                <p data-testid="payment-error" className="mt-4 border border-red-500/40 bg-red-500/10 px-4 py-3 text-xs text-red-400">
                  {payError}
                </p>
              )}
            </div>
          </Reveal>
        </div>
      )}
    </main>
  );
};

export default Panier;
